/**
 * An AG-Grid View that shows data loaded in from the spend API
 */
import React from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { AllModules } from '@ag-grid-enterprise/all-modules';

import { ApButtonSecondary, ApIcon } from '@alixpartners/ui-components';
import isEqual from 'lodash/isEqual';
import { LastUpload } from '../LastUpload';

import {
	updateSpendEditData,
	updateUnpublishedData,
	getHarmonizedData,
	exportPowerPivotCSV,
} from 'utils/apiRequests';
import { setLocalStorageItem, getLocalStorageItem } from 'utils/localStorage';
import { compareDates } from 'utils/date';
import {
	EMPTY_VALUE,
	EMPTY_VALUE_KEY,
	EMPTY_VALUE_NULL,
	EMPTY_VALUE_SPACE,
} from '../../Details/utils';

import { PERMISSIONS } from 'contexts/PermissionsContext';

import {
	COLUMNS,
	TYPES,
	TYPES_CELLS_MAP,
	chartFilterColumns,
	DEFAULT_COLUMN_STATE,
	COLUMN_DEFS,
	FILTER_TYPES,
	DEFAULT_CLICKTHROUGH_COLUMNS,
	SOURCEABLE_COLUMN,
	DEFAULT_CATEGORIES_LIST,
	DEFAULT_CUSTOM_CATEGORIES_LIST,
} from '../tableMetadata';
import {
	ActionsWrapper,
	ActionItem,
	Divider,
	Wrapper,
	RightSection,
} from './styles';
import moment from 'moment';
import createDatasource from './ServerSideDatasource';
import DownloadProgressbar from 'Data/DownloadData/DownloadProgressBar';

const compareOptions = (a, b) => {
	if (!a || !a.name || !b || !b.name || !a.name.localeCompare) {
		return 0;
	}

	return a.name.localeCompare(b.name);
};

const defaultComparator = (a, b) => {
	if ((a === null && b === null) || (a === undefined && b === undefined)) {
		return 0;
	}

	if (a === null || a === undefined) {
		return -1;
	}
	if (b === null || b === undefined) {
		return 1;
	}

	if (typeof a === 'string') {
		return a.localeCompare(b);
	} else {
		return a > b ? 1 : a < b ? -1 : 0;
	}
};

const GRID_NAME = 'AG_GRID_005';
const CURRENCY_COLS = [
	'PO_BASE_SPEND',
	'NET_VALUE_BASE_GBP',
	'NET_VALUE_BASE_USD',
	'NET_VALUE_BASE_EUR',
];
const UNKNOWN_ERROR_MESSAGE =
	'An unknown error has occurred. Please try again later.';
const WRAPPER_THEME_STYLES = {
	height: '100%',
	width: '100vw',
};

// taken from:
// https://www.ag-grid.com/javascript-grid-filter-text/
// filter blank values in a special way
function customComparator(filter, value, filterText) {
	var filterTextLowerCase = filterText.toLowerCase();
	var valueLowerCase = value.toString().toLowerCase();

	const blankMatch =
		filterTextLowerCase === EMPTY_VALUE.toLowerCase() && value === '';

	switch (filter) {
		case 'contains':
			if (blankMatch) {
				return true;
			}
			return valueLowerCase.indexOf(filterTextLowerCase) >= 0;
		case 'notContains':
			return valueLowerCase.indexOf(filterTextLowerCase) === -1;
		case 'equals':
			if (blankMatch) {
				return true;
			}
			return valueLowerCase === filterTextLowerCase;
		case 'notEqual':
			return valueLowerCase !== filterTextLowerCase;
		case 'startsWith':
			return valueLowerCase.indexOf(filterTextLowerCase) === 0;
		case 'endsWith':
			var index = valueLowerCase.lastIndexOf(filterTextLowerCase);
			return (
				index >= 0 &&
				index === valueLowerCase.length - filterTextLowerCase.length
			);
		default:
			// should never happen
			console.warn('invalid filter type ' + filter);
			return false;
	}
}

export class TableView extends React.Component {
	areFiltersApplied = false;
	state = {
		gridOptions: {
			modules: AllModules,
			cacheBlockSize: 200,
			maxBlocksInCache: 10,
			sideBar: true,
			enableColResize: true,
			enableSorting: true,
			enableFilter: true,
			rowModelType: 'serverSide',

			floatingFilter: true,
			maxConcurrentDatasourceRequests: 1,
			multiSortKey: 'ctrl',
		},
		loadingMessageIndex: 0,
		columnDefs: null,
		dataRequested: false,
		defaultColDef: {
			//TODO:for values grouping
			// enableValue: true,
			enableRowGroup: true,
			rowGroup: true,

			sortable: true,
			resizable: true,
			editable: true,
			filter: true,
			pivot: true,
			//TODO:for values grouping
			enablePivot: true,
			comparator: defaultComparator,
		},
		harmonizedOptions: [],
		rowSelection: 'multiple',
		suppressRowClickSelection: false,
		undos: [],
		isSaving: false,
		params: [],
		showDownloadNotification: false,
		errorDownloadingCSV: false,
		colsHash: '',
	};
	filtersData = this.props.filtersData;

	booleanCellCollDef = {
		cellEditor: 'agSelectCellEditor',
		cellEditorParams: {
			values: ['true', 'false'],
		},
	};

	currencyCellCollDef = {
		cellEditor: 'agSelectCellEditor',
		cellEditorParams: {
			values: ['USD', 'EUR', 'GBP'],
		},
	};

	notEditableCellCollDef = {
		editable: false,
	};

	componentDidMount() {
		this.fetchData();
	}

	fetchData = (props = this.props) => {
		const { setIsReady } = props;

		setIsReady(false);

		getHarmonizedData()
			.then(harmonizedOptions => {
				this.setState({
					harmonizedOptions,
				});
				this.buildAgGridData();
			})
			.catch(e => {
				console.error(e);
			});
	};

	/**
	 * Takes the data loaded in from the API and rebuilds it to work with AG-Grid, the examples show ag grid schema.
	 *
	 * @param {object} data - request data response, an object with a data property
	 */
	buildAgGridData = () => {
		const columnDefs = COLUMN_DEFS.map(columnDef => {
			const columnDefCustom = this.getColDefOptions(columnDef);
			const childrenProp = columnDef.children
				? {
						children: columnDef.children.map(childColumnDef => ({
							...childColumnDef,
							...this.getColDefOptions(childColumnDef),
							colId: childColumnDef.field,
							filter:
								FILTER_TYPES[childColumnDef.field] || 'agTextColumnFilter',
							enableRowGroup:
								FILTER_TYPES[childColumnDef.field] === 'agNumberColumnFilter'
									? false
									: true,

							enableValue: true,
							allowedAggFuncs:
								FILTER_TYPES[childColumnDef.field] === 'agNumberColumnFilter'
									? ['sum', 'min', 'max', 'avg', 'count']
									: ['count'],
						})),
				  }
				: {};

			return {
				...columnDef,
				...columnDefCustom,
				...childrenProp,
				colId: columnDef.field,
				filter: FILTER_TYPES[columnDef.field] || 'agTextColumnFilter',
				enableRowGroup:
					FILTER_TYPES[columnDef.field] === 'agNumberColumnFilter'
						? false
						: true,
				enableValue: true,

				allowedAggFuncs:
					FILTER_TYPES[columnDef.field] === 'agNumberColumnFilter'
						? ['sum', 'min', 'max', 'avg', 'count']
						: ['count'],
			};
		});
		this.setState({ columnDefs });
	};
	getColDefOptions(columnDef) {
		const { permissions, isUnpublished, customFilterOptions } = this.props;
		const key = columnDef.field;

		if (
			isUnpublished &&
			!permissions.includes(PERMISSIONS.UnpublishedDataWrite)
		) {
			return this.notEditableCellCollDef;
		}

		if (
			!isUnpublished &&
			!permissions.includes(PERMISSIONS.TransactionsWrite)
		) {
			return this.notEditableCellCollDef;
		}

		let editingColDef = {};

		// Add header labels to custom_data columns
		const customFilterItem =
			customFilterOptions &&
			customFilterOptions.find(option => option.columnName === key);

		if (customFilterItem && customFilterItem.label) {
			editingColDef.headerName = customFilterItem.label;
		}

		if (TYPES_CELLS_MAP[key] === TYPES.boolean) {
			editingColDef = this.booleanCellCollDef;
		}

		if (TYPES_CELLS_MAP[key] === TYPES.currency) {
			editingColDef = this.currencyCellCollDef;
		}

		if (TYPES_CELLS_MAP[key] === TYPES.notEditable) {
			editingColDef = this.notEditableCellCollDef;
		}

		if (key === COLUMNS.DOCUMENT_DATE) {
			editingColDef.filterParams = {
				comparator: compareDates,
			};
		} else {
			editingColDef.filterParams = {
				textCustomComparator: customComparator,
				filterOptions:
					FILTER_TYPES[columnDef.field] === 'agNumberColumnFilter' ||
					FILTER_TYPES[columnDef.field] === 'agDateColumnFilter'
						? [
								'equals',
								'notEqual',
								'lessThan',
								'lessThanOrEqual',
								'greaterThan',
								'greaterThanOrEqual',
						  ]
						: [
								'contains',
								'notContains',
								'equals',
								'notEqual',
								'startsWith',
								'endsWith',
						  ],
				valueGetter: params => {
					return params.data[key] != null ? params.data[key] : '';
				},
			};
		}

		if (FILTER_TYPES[columnDef.field] === 'agSetColumnFilter') {
			const filterValues = this.getFilterValues(columnDef.field);

			if (filterValues) {
				editingColDef.filterParams = { values: filterValues };
			}
		}

		return editingColDef;
	}

	getFilterValues(field) {
		const {
			customFilterOptions,
			companiesList,
			categoriesOptions,
		} = this.props;

		const filterOption = (customFilterOptions || []).find(
			({ columnName }) => field === columnName
		);

		const categoryValues = (categoriesOptions || []).find(
			({ columnName }) => field === columnName
		);

		if (categoryValues) {
			return categoryValues.values.map(({ value }) => value);
		}

		if (filterOption) {
			return filterOption.values.map(({ value }) => value);
		}

		if (field === COLUMNS.SOURCEABLE || field === COLUMNS.IsTailSpend) {
			return [true, false];
		}

		if (field === COLUMNS.SPEND_TYPE) {
			return ['DIRECT', 'INDIRECT'];
		}

		if (field === COLUMNS.PARENT_LEGAL_ENTITY) {
			return companiesList;
		}

		return null;
	}

	shouldComponentUpdate(nextProps, nextState) {
		if (
			this.props.filtersData &&
			(nextProps.filtersData.dateFrom !== this.props.filtersData.dateFrom ||
				nextProps.filtersData.dateTo !== this.props.filtersData.dateTo ||
				nextProps.filtersData.company !== this.props.filtersData.company ||
				nextProps.filtersData.companies !== this.props.filtersData.companies ||
				nextProps.filtersData.harmonized !==
					this.props.filtersData.harmonized ||
				!isEqual(nextProps.filtersData.filters, this.props.filtersData.filters))
		) {
			this.gridApi = null;
			this.areFiltersApplied = false;

			this.setState({ forceGridRedraw: true });

			return false;
		}

		if (
			!isEqual(nextState.undos, this.state.undos) ||
			this.state.isSaving !== nextState.isSaving
		) {
			return true;
		}
		return !this.gridApi;
	}

	onGridReady = params => {
		this.gridApi = params.api;
		this.gridColumnApi = params.columnApi;

		this.setStateFromLocalStorage();

		if (!this.areFiltersApplied) {
			this.applyContextFilters();
		}
		this.areFiltersApplied = true;
		this.props.setIsReady && this.props.setIsReady(true);
		const {
			columnDefs,
			defaultColDef,
			rowSelection,
			suppressRowClickSelection,
		} = this.state;
		const gridOptions = {
			columnDefs,
			defaultColDef,
			rowSelection,
			suppressRowClickSelection,
			...this.state.gridOptions,
		};
		const datasource = createDatasource(
			gridOptions,
			this.paramsSetter,
			params.columnApi,
			this.colsHashSetter,
			this.fetchGridOptions,
			params.api
		);
		this.gridApi.setServerSideDatasource(datasource);
	};
	paramsSetter = params => {
		this.setState({ params: params });
	};
	colsHashSetter = () => {
		this.forceUpdate();
	};
	fetchGridOptions = () => {
		return this.state.gridOptions;
	};
	getValueFromMoreFilters = key => {
		const { filtersData } = this.props;

		const item = filtersData.filters.find(
			({ columnName }) => key === columnName
		);

		if (item) {
			return item.value;
		}

		return null;
	};

	applyContextFilters = () => {
		const { filtersData, isCustomCategory, customFilterOptions } = this.props;

		if (this.gridApi && filtersData) {
			// set one of the chart filter columns
			Object.keys(chartFilterColumns).forEach(key => {
				let filterValue =
					filtersData[key] ||
					(filtersData.filters ? this.getValueFromMoreFilters(key) : null);
				const columnName = chartFilterColumns[key];
				const instance = this.gridApi.getFilterInstance(COLUMNS[columnName]);

				if (instance) {
					if (filterValue && key === 'spendType') {
						instance.setModel({
							type: 'set',
							values: [filterValue],
						});
					} else if (
						filterValue &&
						(key === 'companies' || key === 'company')
					) {
						instance.setModel({
							type: 'set',
							values: Array.isArray(filterValue) ? filterValue : [filterValue],
						});
					} else if (!!filterValue) {
						if (key === 'monthYearDate') {
							const date = moment(filterValue, 'YYYY MMM');
							instance.setModel({
								type: 'contains',
								filter: date.format('YYYY-MM'),
							});
						} else if (
							key === 'isTailSpend' ||
							(key && key.indexOf('custom_data') === 0)
						) {
							const selectedCustomFilterOption = customFilterOptions.find(
								option => option.columnName === key
							);

							const transformFilterValue = filterValue => {
								if (filterValue === EMPTY_VALUE_NULL) {
									return null;
								} else if (
									filterValue === EMPTY_VALUE_SPACE ||
									filterValue === EMPTY_VALUE_KEY
								) {
									return '';
								} else if (selectedCustomFilterOption) {
									const filterValueFromOption = selectedCustomFilterOption.values.find(
										item =>
											(item.value ? item.value.toLowerCase() : item.value) ===
											(filterValue ? filterValue.toLowerCase() : filterValue)
									);
									if (filterValueFromOption) {
										return filterValueFromOption.value;
									}

									return filterValue;
								} else {
									return filterValue;
								}
							};

							if (Array.isArray(filterValue)) {
								filterValue = filterValue.map(transformFilterValue);
							} else {
								filterValue = transformFilterValue(filterValue);
							}

							instance.setModel({
								type: 'set',
								values: Array.isArray(filterValue)
									? filterValue
									: [filterValue],
							});
						} else {
							if (
								filterValue === EMPTY_VALUE_NULL ||
								filterValue === EMPTY_VALUE_SPACE ||
								filterValue === EMPTY_VALUE_KEY
							) {
								filterValue = EMPTY_VALUE;
								// NOTE: workaround - as blank string and null are not supported
								// https://github.com/ag-grid/ag-grid/issues/2712
							}

							instance.setModel({
								type: 'equals',
								filter: filterValue,
								filterTo: null,
							});
						}
					}
				}
			});

			// set custom column value
			if (filtersData.customColumnKey && filtersData.customColumnValue) {
				const key = filtersData.customColumnKey;
				if (!!key.match(/^customfilter\d+$/)) {
					const columnName = key.replace('filter', '_data');
					const instance = this.gridApi.getFilterInstance(COLUMNS[columnName]);
					const filterValue = filtersData.customColumnValue;
					if (instance) {
						instance.setModel({
							type: 'equals',
							filter: filterValue,
							filterTo: null,
						});
					}
				}
			}

			// adding in all hierarchy values as filters if they exist
			if (filtersData.hierarchy) {
				const customCategories = [
					COLUMNS.Custom_L1Category,
					COLUMNS.Custom_L2Category,
					COLUMNS.Custom_L3Category,
					COLUMNS.Custom_L4Category,
				];
				const categories = [
					COLUMNS.AP_L1Category,
					COLUMNS.AP_L2Category,
					COLUMNS.AP_L3Category,
					COLUMNS.AP_L4Category,
				];
				const hieararchyColumns = (isCustomCategory
					? customCategories
					: categories
				).map((item, index) => ({
					name: item,
					value: filtersData.hierarchy[index],
				}));

				hieararchyColumns.forEach(item => {
					if (item.value) {
						const instance = this.gridApi.getFilterInstance(item.name);

						if (instance) {
							instance.setModel({
								type: 'equals',
								filter: item.value,
								filterTo: null,
							});
						}
					}
				});
			}
			this.gridApi.onFilterChanged();
		}
	};

	resetAllFilters = () => {
		if (this.gridApi) {
			this.gridApi.setFilterModel(null);
		}
		this.updateQueryString();
	};
	exportCSV = async () => {
		try {
			await exportPowerPivotCSV(this.state.params);
			this.setState({ showDownloadNotification: true });
			this.forceUpdate();
		} catch (e) {
			this.setState({
				showDownloadNotification: true,
				errorDownloadingCSV: true,
			});
			this.forceUpdate();
		}
		setTimeout(() => {
			this.handleCloseNotification();
			this.forceUpdate();
		}, 5000);
	};
	handleCloseNotification = () => {
		this.setState({
			showDownloadNotification: false,
			errorDownloadingCSV: false,
		});
		this.forceUpdate();
	};

	handleColumnsChange = ({ columnApi }) => {
		if (!columnApi || !this.areFiltersApplied) {
			return;
		}

		const columnState = columnApi.getColumnState();

		setLocalStorageItem(GRID_NAME + '-GRID-COLUMNS', columnState);
	};

	handleCellValueChange = event => {
		const selectedRowNodes = this.gridApi.getSelectedNodes();

		if (
			event.oldValue &&
			event.newValue &&
			event.oldValue.toString() === event.newValue.toString() &&
			selectedRowNodes.length === 1
		) {
			return;
		}

		const { showErrorMessage } = this.props;
		const { undos } = this.state;
		const updatedColId = event.column.colId;
		let undoData = [];
		let data = [];

		if (CURRENCY_COLS.indexOf(updatedColId) !== -1) {
			const value = Number(event.value);
			const undoValue = Number(event.oldValue);

			if (!isNaN(value)) {
				data = selectedRowNodes.map(rowNode => {
					const updatedRow = { ...rowNode.data };
					const undoRow = { ...rowNode.data };

					CURRENCY_COLS.forEach(colId => {
						const undoColumnValue = undoRow[colId];

						updatedRow[colId] = value;
						undoRow[colId] =
							colId === updatedColId && event.data.ID === rowNode.data.ID
								? undoValue
								: undoColumnValue;
					});

					undoData = [...undoData, undoRow];

					rowNode.setData(updatedRow);

					return updatedRow;
				});
			} else {
				data = {
					...event.data,
				};
				undoData = [{ ...event.data, [updatedColId]: event.oldValue }];
			}
		} else {
			data = selectedRowNodes.map(rowNode => {
				const undoColumnValue = rowNode.data[updatedColId];
				const updatedData = {
					...rowNode.data,
					[updatedColId]: event.value,
				};
				undoData = [
					...undoData,
					{
						...rowNode.data,
						[updatedColId]:
							rowNode.data.ID === event.data.ID
								? event.oldValue
								: undoColumnValue,
					},
				];

				rowNode.setData(updatedData);

				return updatedData;
			});
		}

		if (undoData.length > 0) {
			this.setState({ undos: [...undos, undoData] });
		}

		return this.saveSpendEdit(data).catch(error => {
			this.setState({ isSaving: false });
			this.undo(true);

			// show validation errors only when 400 Bad request message is received
			// fix to prevent two dialogs from appearing
			if (error.response && error.response.status === 400) {
				showErrorMessage &&
					showErrorMessage(
						(error && error.errorMessage) || UNKNOWN_ERROR_MESSAGE
					);
			}
		});
	};

	saveSpendEdit = data => {
		const { clearApiCache, isUnpublished } = this.props;
		this.setState({ isSaving: true });
		const updateRequest = isUnpublished
			? updateUnpublishedData
			: updateSpendEditData;

		return updateRequest(
			data.map(item => ({
				...item,
				PO_CREATION_DATE: moment(item.PO_CREATION_DATE).format('YYYY-MM-DD'),
			}))
		).then(rsp => {
			this.setState({ isSaving: false });
			if (clearApiCache) {
				clearApiCache();
			}

			return rsp;
		});
	};

	undo = shouldNotSave => {
		const { undos } = this.state;
		const lastActionData = undos.slice(-1)[0];
		const remainingUndos = undos.slice(0, -1);

		this.setState({ undos: remainingUndos });
		this.setDataToRows(lastActionData);
		if (!shouldNotSave) {
			this.saveSpendEdit(lastActionData);
		}
	};

	setDataToRows = data => {
		if (this.gridApi) {
			data.forEach(rowData => {
				const rowNode = this.gridApi.getRowNode(rowData.ID);
				const flashColumns = [];

				Object.keys(rowNode.data).forEach(key => {
					if (rowNode.data[key] !== rowData[key]) {
						flashColumns.push(key);
					}
				});

				this.gridApi.flashCells({
					rowNodes: [rowNode],
					columns: flashColumns,
				});
				rowNode.setData(rowData);
			});
		}
	};

	isExternalFilterPresent = () => {
		return true;
	};

	deselectAllRows = () => {
		if (this.gridApi) {
			this.gridApi.deselectAll();
		}
	};

	handleFilterChange = () => {
		this.deselectAllRows();
	};

	updateQueryString = () => {
		const { contextParams } = this.props;

		if (contextParams) {
			contextParams.setQueryStringStateAndPush({
				storedQueryId: undefined,
				customFiltersValues: [],
				customFilters: [],
			});
		}
	};

	getCurrentColumnsState = () => {
		const { filtersData, isCustomCategory } = this.props;
		let currentState =
			getLocalStorageItem(GRID_NAME + '-GRID-COLUMNS') || DEFAULT_COLUMN_STATE;

		if (filtersData && (filtersData.supplierId || filtersData.hierarchy)) {
			let mandatoryColumns = !isCustomCategory
				? [
						...DEFAULT_CLICKTHROUGH_COLUMNS,
						...DEFAULT_CATEGORIES_LIST,
						SOURCEABLE_COLUMN,
				  ]
				: [
						...DEFAULT_CLICKTHROUGH_COLUMNS,
						...DEFAULT_CUSTOM_CATEGORIES_LIST,
						SOURCEABLE_COLUMN,
				  ];
			mandatoryColumns = mandatoryColumns.filter(
				col => !currentState.find(item => col.colId === item.colId)
			);
			currentState = [...mandatoryColumns, ...currentState];
		}

		return currentState;
	};

	setStateFromLocalStorage = () => {
		const colState = this.getCurrentColumnsState();

		if (colState !== null && this.gridColumnApi !== null) {
			this.gridColumnApi.setColumnState(colState);
		}
	};

	handleCellDoubleClicked = api => {
		const { permissions, isUnpublished } = this.props;

		if (
			isUnpublished &&
			!permissions.includes(PERMISSIONS.UnpublishedDataWrite)
		) {
			return;
		}

		if (
			!isUnpublished &&
			!permissions.includes(PERMISSIONS.TransactionsWrite)
		) {
			return;
		}

		if (api.column.colId === COLUMNS.AP_L4Category) {
			const editingRowIndex = api.rowIndex;
			const editingRowNode = this.gridApi.getDisplayedRowAtIndex(
				editingRowIndex
			);

			if (!editingRowNode) {
				return;
			}

			this.props.openCategorisationModal({
				editingRowNode,
				selectedRowNodes: this.gridApi.getSelectedNodes(),
				onSave: this.handleTaxonomyEditorSave,
			});
		}

		if (api.column.colId === COLUMNS.HARMONIZED_VENDOR_NAME) {
			const editingRowIndex = api.rowIndex;
			const editingRowNode = this.gridApi.getDisplayedRowAtIndex(
				editingRowIndex
			);

			if (!editingRowNode) {
				return;
			}

			this.props.openCellModalEditor({
				value: editingRowNode.data[COLUMNS.HARMONIZED_VENDOR_NAME],
				editingRowNode,
				options: this.state.harmonizedOptions
					.map(({ name }) => ({
						name,
						value: name,
					}))
					.sort(compareOptions),
				allowNewOption: true,
				onSave: this.handleVendorNameSave,
			});
		}
	};

	handleVendorNameSave = ({ value: supplierName, editingRowNode }) => {
		const selectedRowNodes = this.gridApi.getSelectedNodes();

		if (
			supplierName &&
			editingRowNode.data[COLUMNS.HARMONIZED_VENDOR_NAME] &&
			supplierName.toString() ===
				editingRowNode.data[COLUMNS.HARMONIZED_VENDOR_NAME].toString() &&
			selectedRowNodes.length === 1
		) {
			return;
		}

		const { harmonizedOptions, undos } = this.state;
		let undoData = [];

		let harmonizedObj = harmonizedOptions.find(
			item => item.name === supplierName
		);

		if (!harmonizedObj) {
			harmonizedObj = {
				name: supplierName,
				id: null,
			};
		}

		const updatedProps = {
			[COLUMNS.HARMONIZED_VENDOR_NAME]: supplierName,
			[COLUMNS.HARMONIZED_VENDOR_ID]: harmonizedObj.id,
		};

		const updateData = selectedRowNodes.map(rowNode => {
			const updatedRow = { ...rowNode.data, ...updatedProps };

			undoData = [...undoData, rowNode.data];
			rowNode.setData(updatedRow);

			return updatedRow;
		});

		if (undoData.length > 0) {
			this.setState({ undos: [...undos, undoData] });
		}

		this.saveSpendEdit(updateData).then(editedRows => {
			// Update harmonized id for new vendor after save
			if (!harmonizedObj.id) {
				const newHarmonizedId = editedRows[0][COLUMNS.HARMONIZED_VENDOR_ID];
				// Update harmonized options with new id
				this.setState({
					harmonizedOptions: [
						{
							...harmonizedObj,
							id: newHarmonizedId,
						},
						...harmonizedOptions,
					],
				});

				selectedRowNodes.forEach(rowNode => {
					const updatedProps = {
						[COLUMNS.HARMONIZED_VENDOR_ID]: newHarmonizedId,
					};

					rowNode.setData({ ...rowNode.data, ...updatedProps });
				});
			}
		});
	};

	handleTaxonomyEditorSave = ({ taxonomy, selectedRowNodes }) => {
		const { undos } = this.state;
		let undoData = [];

		if (!taxonomy) {
			return;
		}

		const updatedProps = {
			AP_L1Category: taxonomy.project_L1Category,
			AP_L2Category: taxonomy.project_L2Category,
			AP_L3Category: taxonomy.project_L3Category,
			AP_L4Category: taxonomy.project_L4Category,
			...(taxonomy.aP_L4Code
				? {
						AP_L1Code: taxonomy.aP_L1Code,
						AP_L2Code: taxonomy.aP_L2Code,
						AP_L3Code: taxonomy.aP_L3Code,
						AP_L4Code: taxonomy.aP_L4Code,
				  }
				: {}),
		};

		const updateData = selectedRowNodes.map(rowNode => {
			const updatedRow = { ...rowNode.data, ...updatedProps };

			undoData = [...undoData, rowNode.data];
			rowNode.setData(updatedRow);

			return updatedRow;
		});

		if (undoData.length > 0) {
			this.setState({ undos: [...undos, undoData] });
		}

		return this.saveSpendEdit(updateData);
	};

	handleCellFocus = e => {
		if (e && e.api) {
			const selectedRows = e.api.getSelectedNodes().map(i => i.rowIndex);
			if (selectedRows.includes(e.rowIndex)) {
				e.api.gridOptionsWrapper.gridOptions.suppressRowClickSelection = true;
			} else {
				e.api.gridOptionsWrapper.gridOptions.suppressRowClickSelection = false;
			}
		}
	};

	onCellKeyDown = ({ event }) => {
		if (event.key.toLowerCase() !== 'z') {
			return;
		}

		const { undos } = this.state;

		if (
			undos.length > 0 &&
			((navigator.appVersion.indexOf('Mac') !== -1 && event.metaKey) ||
				(navigator.appVersion.indexOf('Mac') === -1 && event.ctrlKey))
		) {
			this.undo();
		}
	};

	getUndoTitle = () => {
		const metaKeyName =
			navigator.appVersion.indexOf('Mac') !== -1 ? '⌘Cmd' : 'Ctrl';

		return `Press ${metaKeyName}+Z to undo your last action`;
	};

	getRowNodeId = data => data.ID;

	/**
	 * Render out the ag grid after the data state property has been set
	 */
	render() {
		const {
			columnDefs,
			defaultColDef,
			rowSelection,
			suppressRowClickSelection,
			undos,
			isSaving,
			forceGridRedraw,
			showDownloadNotification,
			errorDownloadingCSV,
		} = this.state;

		const { isUnpublished, timestamp } = this.props;

		if (!columnDefs) {
			return null;
		}

		if (forceGridRedraw) {
			setTimeout(() => this.setState({ forceGridRedraw: false }));

			return null;
		}
		const isUndoDisabled = isSaving || undos.length === 0;

		const gridOptions = {
			...this.state.gridOptions,
			columnDefs,
			defaultColDef,
			rowSelection,
			suppressRowClickSelection,
			onGridReady: this.onGridReady,
		};

		return (
			<Wrapper>
				<ActionsWrapper isUnpublished={isUnpublished}>
					<ActionItem title={!isUndoDisabled ? this.getUndoTitle() : undefined}>
						<ApButtonSecondary
							iconName="undo"
							disabled={isUndoDisabled}
							onClick={() => this.undo()}
						>
							Undo
						</ApButtonSecondary>
					</ActionItem>
					<Divider />
					<ActionItem>
						<ApButtonSecondary onClick={this.resetAllFilters}>
							Reset All Filters
						</ApButtonSecondary>
					</ActionItem>
					<ActionItem>
						<ApButtonSecondary onClick={this.exportCSV}>
							<ApIcon iconName="outline_save_alt" iconColor="black" />
							&nbsp; Download CSV
						</ApButtonSecondary>
					</ActionItem>
					<RightSection>
						<LastUpload timestamp={timestamp} isUnpublished={isUnpublished} />
					</RightSection>
				</ActionsWrapper>
				<div style={WRAPPER_THEME_STYLES} className="ag-theme-balham">
					<AgGridReact {...gridOptions} />
				</div>

				{showDownloadNotification && (
					<DownloadProgressbar
						error={errorDownloadingCSV}
						failMessage="Error generating file"
						successMessage="Generating file... You will receive a notification when the file has been generated"
						onClose={this.handleCloseNotification}
					/>
				)}
			</Wrapper>
		);
	}
}
